import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  InGameCurrency,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import bestFiveClue from '@sorare/core/src/assets/crafting-clues/best-five.webp';
import bestStarRankClue from '@sorare/core/src/assets/crafting-clues/best-star-rank.webp';
import competitionClue from '@sorare/core/src/assets/crafting-clues/competition.webp';
import divisionClue from '@sorare/core/src/assets/crafting-clues/division.webp';
import countryClue from '@sorare/core/src/assets/crafting-clues/nationality.webp';
import positionClue from '@sorare/core/src/assets/crafting-clues/position.webp';
import positionClueMlb from '@sorare/core/src/assets/crafting-clues/positionMlb.webp';
import shuffleClue from '@sorare/core/src/assets/crafting-clues/shuffle.webp';
import { Amount } from '@sorare/core/src/atoms/Amount';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { GameplayTierStars } from '@sorare/core/src/components/card/GameplayTierStars';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { GAMEPLAY_TIER_LABELS } from '@sorare/core/src/lib/gameplayTiers';
import { glossary, marketCredits, wheel } from '@sorare/core/src/lib/glossary';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import { qualityNames } from '@sorare/core/src/lib/players';
import { getRewardImageUrl, isXPCurrency } from '@sorare/core/src/lib/rewards';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';
import {
  isGoldenWheelTicketCurrency,
  isWheelTicketCurrency,
} from '@sorare/core/src/lib/wheelCurrencies';

import { getXpIllustration } from '#src/lib/wheelReward.ts';

import type { useWheelReward_probabilisticBundleSlotConfig } from './__generated__/utils.graphql.ts';
import gemUrl from './assets/gem.webp';
import jerseyUrl from './assets/jersey.webp';
import nothingUrl from './assets/nothing.svg';
import respinUrl from './assets/respin.svg';
import ticketUrl from './assets/ticket.webp';

type WheelReward = {
  illustration: string;
  description: ReactNode;
  longDescription?: ReactNode;
  amount?: ReactNode;
  rewardName?: ReactNode;
};

const PlayerImageWrapper = styled(ResponsiveImg)`
  border: 1px solid var(--c-nd-300);
  background-color: var(--c-nl-500);
  border-radius: 50%;
  aspect-ratio: 1;
  width: 40%;
  object-fit: contain;
`;

export const useWheelReward = withFragments(
  (config: useWheelReward_probabilisticBundleSlotConfig): WheelReward => {
    const { toMonetaryAmount } = useMonetaryAmount();
    const { sport } = useSportContext();
    const { formatMessage } = useIntl();

    if (config.deliverableItems?.length) {
      const { deliverableItem } = config.deliverableItems[0];

      if (isType(deliverableItem, 'JerseyDeliverableItem')) {
        return {
          illustration: deliverableItem.pictureUrl || jerseyUrl,
          description:
            deliverableItem.name ||
            (deliverableItem.signed
              ? formatMessage({
                  defaultMessage: 'SIGNED JERSEY',
                })
              : formatMessage({
                  defaultMessage: 'JERSEY',
                })),
        };
      }
      if (isType(deliverableItem, 'TicketsDeliverableItem')) {
        return {
          illustration: ticketUrl,
          description: formatMessage({
            defaultMessage: 'TICKET',
          }),
        };
      }

      return {
        illustration: deliverableItem.pictureUrl,
        description: deliverableItem.name,
      };
    }

    // card
    if (config.cards?.length) {
      const card = config.cards[0];
      const cardPlayers = card.players || [];

      const seriesLabel = card.gameplayTier ? (
        <Vertical center>
          <GameplayTierStars gameplayTier={card.gameplayTier} />
          {GAMEPLAY_TIER_LABELS[card.gameplayTier]}
        </Vertical>
      ) : undefined;

      const cardQuality = card.quality
        ? qualityNames[card.quality]
        : formatMessage(glossary.card);

      const playerName =
        cardPlayers.length === 1 ? cardPlayers[0]?.abbreviatedName : undefined;

      const description = seriesLabel || cardQuality;

      return {
        illustration: getRewardImageUrl('card', card.rarity),

        // hack to display the player underneath the illustration
        description: (
          <Vertical>
            {cardPlayers.length > 0 && (
              <Horizontal center>
                {cardPlayers.map(({ slug, avatarPictureUrl }) => (
                  <PlayerImageWrapper
                    key={slug}
                    src={avatarPictureUrl}
                    alt=""
                    width={60}
                    cropWidth={80}
                  />
                ))}
              </Horizontal>
            )}
            {playerName || description}
          </Vertical>
        ),
        longDescription: description,
        rewardName: description,
      };
    }

    // card shard
    if (config.cardShards?.length) {
      const cardShard = config.cardShards[0];

      return {
        illustration: getRewardImageUrl(
          'shard',
          cardShard.cardSet?.slug || cardShard.rarity
        ),
        description: `${cardShard.quantity}`,
        longDescription: formatMessage(glossary.essenceRarityAmountLabel, {
          amount: cardShard.quantity,
          rarity: cardShard.cardSet?.name ?? cardShard.rarity,
        }),
        rewardName: formatMessage(glossary.essence),
        amount: cardShard.quantity,
      };
    }

    // conversion credit
    if (config.conversionCredit) {
      return {
        illustration: 'https://frontend-assets.sorare.com/rewards/credit.png',
        description: (
          <Vertical>
            <Amount
              monetaryAmount={toMonetaryAmount(
                config.conversionCredit.maxDiscount
              )}
            />

            <BrandXS color="var(--c-nd-500)">
              <FormattedNumber
                value={config.conversionCredit.percentageDiscount}
                style="percent"
              />
            </BrandXS>
          </Vertical>
        ),
        longDescription: formatMessage(marketCredits.description, {
          percentage: config.conversionCredit.percentageDiscount,
          maxDiscount: (
            <Amount
              monetaryAmount={toMonetaryAmount(
                config.conversionCredit.maxDiscount
              )}
            />
          ),
        }),
        amount: (
          <Amount
            monetaryAmount={toMonetaryAmount(
              config.conversionCredit.maxDiscount
            )}
          />
        ),
        rewardName: formatMessage(marketCredits.marketCredits),
      };
    }

    if (config.inGameCurrencies?.length) {
      const igc = config.inGameCurrencies.filter(
        x => !isGoldenWheelTicketCurrency(x.currency)
      )[0];

      if (igc) {
        const data: Record<
          | 'DIVISION_CRAFT_CLUE'
          | 'POSITION_CRAFT_CLUE'
          | 'COMPETITION_CRAFT_CLUE'
          | 'COUNTRY_CRAFT_CLUE'
          | 'SPECIAL_EVENT_CRAFT_CLUE'
          | 'FIFTY_FIFTY_CRAFT_CLUE'
          | 'BEST_STAR_RANK_CRAFT_CLUE'
          | 'COMMON_GEM',
          { picture: string; name: MessageDescriptor }
        > = {
          [InGameCurrency.DIVISION_CRAFT_CLUE]: {
            picture: divisionClue,
            name: wheel.divisionClue,
          },
          [InGameCurrency.POSITION_CRAFT_CLUE]: {
            picture: sport === Sport.BASEBALL ? positionClueMlb : positionClue,
            name: wheel.positionClue,
          },
          [InGameCurrency.COMPETITION_CRAFT_CLUE]: {
            picture: competitionClue,
            name: wheel.competitionClue,
          },
          [InGameCurrency.COUNTRY_CRAFT_CLUE]: {
            picture: countryClue,
            name: wheel.countryClue,
          },
          [InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE]: {
            picture: shuffleClue,
            name: wheel.shuffleClue,
          },
          [InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE]: {
            picture: bestFiveClue,
            name: wheel.bestFiveClue,
          },
          [InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE]: {
            picture: bestStarRankClue,
            name: wheel.bestStarRankClue,
          },
          [InGameCurrency.COMMON_GEM]: {
            picture: gemUrl,
            name: wheel.gems,
          },
        };
        if (igc.currency in data) {
          const d = data[igc.currency as keyof typeof data];
          return {
            illustration: d.picture,
            description: (
              <>
                {igc.amount} {formatMessage(d.name)}
              </>
            ),
          };
        }

        // XP
        if (isXPCurrency(igc?.currency)) {
          const xp = igc.amount;
          const xpRarity = inGameCurrencyToRarity[igc.currency];
          return {
            illustration:
              xpRarity && xpRarity !== Rarity.limited
                ? getRewardImageUrl('xp', xpRarity)
                : getXpIllustration(xp),
            description: (
              <>
                {igc.amount} {formatMessage(glossary.xp)}
              </>
            ),
            amount: (
              <>
                {igc.amount} {formatMessage(glossary.xp)}
              </>
            ),
            rewardName: ' ',
          };
        }

        // respin
        if (isWheelTicketCurrency(igc.currency)) {
          return {
            illustration: respinUrl,
            description: formatMessage(wheel.spinAgain),
          };
        }
      }
    }

    return {
      // FIXME
      illustration: nothingUrl,
      description: formatMessage(wheel.nothing),
    };
  },
  {
    probabilisticBundleSlotConfig: gql`
      fragment useWheelReward_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
        id
        deliverableItems {
          quantity
          deliverableItem {
            slug
            pictureUrl
            name
            ... on JerseyDeliverableItem {
              slug
              signed
            }
          }
        }
        cards {
          quality
          rarity
          gameplayTier
          players {
            slug
            avatarPictureUrl
            abbreviatedName
          }
        }
        conversionCredit {
          id
          maxDiscount {
            ...useMonetaryAmount_monetaryAmount
          }
          percentageDiscount
        }
        inGameCurrencies {
          id
          amount
          currency
        }
        cardShards {
          id
          quantity
          rarity
          cardSet {
            slug
            name
          }
        }
      }

      ${useMonetaryAmount.fragments.monetaryAmount}
    ` as TypedDocumentNode<useWheelReward_probabilisticBundleSlotConfig>,
  }
);
